import { forwardRef } from 'react';

import type { IconButtonProps, PaperProps, PopperProps } from '@mui/material';
import { Autocomplete, Popper, Stack, TextField, Typography } from '@mui/material';

import { UserAvatar, UserChip } from '../../../../../components/base';
import { useFullScreen } from '../../../../../components/FullScreen';
import type { User } from '../../../../../types';
import getFullName from '../../../../../utils/getFullName';

type Props = {
  values: User[] | undefined;
  onChange: (users: User[] | undefined) => void;
  label?: string;
  placeholder?: string;
  options: User[];
  disabled?: boolean;
  componentsProps?: {
    clearIndicator?: Partial<IconButtonProps>;
    paper?: PaperProps;
    popper?: Partial<PopperProps>;
    popupIndicator?: Partial<IconButtonProps>;
  };
  onBlur?: () => void;
  maxWidthInput?: number;
};

const CustomPopper = (props: PopperProps) => {
  const { ref } = useFullScreen();
  return (
    <Popper
      {...props}
      container={ref.current}
      sx={{
        zIndex: 'drawer',
      }}
      className='ag-custom-component-popup'
    />
  );
};

export const MultiUsersEditor = forwardRef(
  ({ values, onChange, options, label, placeholder, componentsProps, onBlur, disabled, maxWidthInput }: Props, ref) => (
    <Autocomplete
      disabled={disabled}
      onBlur={onBlur}
      componentsProps={componentsProps}
      ref={ref}
      multiple
      disableCloseOnSelect
      options={options}
      filterSelectedOptions
      value={values}
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      onChange={(_, newValues) => onChange(newValues)}
      getOptionLabel={(user: User) => getFullName(user)}
      renderTags={(value, getTagProps) =>
        value.map((user, index) => <UserChip user={user} variant='outlined' size='small' {...getTagProps({ index })} />)
      }
      renderInput={(params) => (
        <TextField
          sx={{ minWidth: '160px', maxWidth: maxWidthInput }}
          {...params}
          label={label}
          placeholder={placeholder}
        />
      )}
      renderOption={(props, user) => (
        <Typography component='li' noWrap {...props}>
          <Stack component='span' direction='row' spacing={2} minWidth={0}>
            <UserAvatar user={user} sx={{ marginRight: '8px' }} />
            <Typography textOverflow='ellipsis' overflow='hidden'>
              {getFullName(user)}
            </Typography>
          </Stack>
        </Typography>
      )}
      PopperComponent={CustomPopper}
    />
  ),
);
